/**
 * TaskExtractMenu — Message context menu の "AI でタスク化" エントリポイント.
 *
 * 設計書: docs/chatwork_killer/07_ai_voice_video.md セクション 6.2
 *
 * 振る舞い:
 *   - 1) ボタンをクリック → POST /v1/messages/:id/extract-tasks を発行.
 *   - 2) 候補が返ってきたら確認モーダルを開く.
 *   - 3) title / due / 概要を編集可能. 他者への割当権限がある場合だけ、API の照合結果
 *        (assignee_user_id) を初期選択にし、現メンバーから選び直せる。通常担当者は本人に固定する。
 *   - 4) "タスクを作成" を押すと props.onCreate(task) を呼ぶ. (実体の POST /v1/tasks 呼び出しは
 *        親コンポーネントが #5993 のクライアントを使って行う.)
 *
 * Props:
 *   - messageId: 対象 message
 *   - messages:   この message を含む直近 N 件 (タスク抽出対象)
 *   - members:    担当者として選べるワークスペースの現メンバー
 *   - onCreate:   confirm 後のコールバック. タスク作成 API を叩くのは呼び出し側.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ListChecks, Loader2 } from "lucide-react";
import {
  fetchExtractTasks,
  type ExtractTaskMessageInput,
  type ExtractedTaskCandidate,
} from "./api";
import { cn } from "@/lib/cn";

export interface TaskExtractMenuProps {
  messageId: string;
  messages: ExtractTaskMessageInput[];
  /** モーダル confirm 時のコールバック. 親側で /v1/tasks (#5993) を叩く. */
  onCreate: (task: {
    title: string;
    description?: string;
    assignee_hint: string | null;
    /** 選択された担当者の user_id (未割り当ては null). POST /v1/tasks の assignee に渡す */
    assignee: string | null;
    due_date_hint: string | null;
    source_message_id: string;
  }) => Promise<void> | void;
  /** 担当者として選べるワークスペースの現メンバー */
  members?: { id: string; displayName: string }[];
  /** 権限を確認できるまで、他者への割当とタスク登録は許可しない。 */
  canAssign?: boolean;
  canCreate?: boolean;
  currentUserId?: string;
  locale?: "ja-JP" | "en-US";
  fetcher?: typeof fetchExtractTasks;
}

interface EditableTask extends ExtractedTaskCandidate {
  selected: boolean;
  /** 画面で選ばれている担当者の user_id. 未割り当ては null */
  assignee: string | null;
}

export function TaskExtractMenu(props: TaskExtractMenuProps): JSX.Element {
  const { t } = useTranslation();
  const {
    messageId,
    messages,
    onCreate,
    members = [],
    canAssign = false,
    canCreate = false,
    currentUserId,
    locale = "ja-JP",
    fetcher = fetchExtractTasks,
  } = props;
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error" | "creating">("idle");
  const [candidates, setCandidates] = useState<EditableTask[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function run(): Promise<void> {
    setOpen(true);
    setStatus("loading");
    setError(null);
    try {
      const res = await fetcher(messageId, { messages, locale });
      setCandidates(
        res.candidates.map((c) => ({
          ...c,
          selected: true,
          assignee: !canAssign
            ? currentUserId ?? null
            : c.assignee_user_id && members.some((m) => m.id === c.assignee_user_id)
              ? c.assignee_user_id
              : null,
        })),
      );
      setStatus("ready");
    } catch (e) {
      setStatus("error");
      setError(e instanceof Error ? e.message : "extract-tasks failed");
    }
  }

  async function confirm(): Promise<void> {
    if (!canCreate) return;
    setStatus("creating");
    try {
      const selected = candidates.filter((c) => c.selected);
      for (const c of selected) {
        await onCreate({
          title: c.title,
          description: c.description,
          assignee_hint: c.assignee_hint,
          assignee: canAssign ? c.assignee : currentUserId ?? null,
          due_date_hint: c.due_date_hint,
          source_message_id: c.source_message_id,
        });
      }
      setOpen(false);
      setCandidates([]);
      setStatus("idle");
    } catch (e) {
      setStatus("ready");
      setError(e instanceof Error ? e.message : "create failed");
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => void run()}
        className={cn(
          "inline-flex items-center gap-1.5 px-3 py-1.5 rounded",
          "text-sm text-fg-secondary",
          "hover:bg-surface-raised hover:text-fg-primary",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600",
        )}
        aria-label={t("ai.extractTasks.button", { defaultValue: "AI でタスク化" }) as string}
        data-testid="task-extract-button"
      >
        <ListChecks size={16} aria-hidden />
        {t("ai.extractTasks.button", { defaultValue: "AI でタスク化" }) as string}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-fg-primary/40 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="task-extract-title"
          data-testid="task-extract-modal"
        >
          <div className="bg-canvas rounded-lg shadow-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto">
            <header className="px-4 py-3 border-b border-border-subtle flex items-center">
              <h2 id="task-extract-title" className="font-bold text-base">
                {t("ai.extractTasks.title", { defaultValue: "タスク候補を確認" }) as string}
              </h2>
              <span className="flex-1" />
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-fg-muted hover:text-fg-primary"
                aria-label={t("common.close", { defaultValue: "閉じる" }) as string}
              >
                ×
              </button>
            </header>
            <div className="px-4 py-3 space-y-3">
              {status === "loading" && (
                <div className="flex items-center gap-2 text-fg-muted text-sm" aria-live="polite">
                  <Loader2 size={14} aria-hidden className="animate-spin" />
                  {t("ai.extractTasks.loading", { defaultValue: "タスクを抽出中..." }) as string}
                </div>
              )}
              {status === "error" && (
                <div className="text-danger text-sm" role="alert">
                  {error}
                </div>
              )}
              {status !== "loading" && candidates.length === 0 && status !== "error" && (
                <p className="text-fg-muted text-sm">
                  {t("ai.extractTasks.empty", { defaultValue: "抽出できるタスク候補がありませんでした" }) as string}
                </p>
              )}
              {candidates.map((c, i) => (
                <article
                  key={`${c.source_message_id}-${i}`}
                  className={cn(
                    "rounded border p-3 space-y-2",
                    c.selected ? "border-brand-600 bg-brand-50/30" : "border-border-subtle bg-canvas",
                  )}
                  data-testid={`task-candidate-${i}`}
                >
                  <header className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={c.selected}
                      onChange={(e) =>
                        setCandidates((prev) =>
                          prev.map((p, j) => (i === j ? { ...p, selected: e.target.checked } : p)),
                        )
                      }
                      aria-label={t("ai.extractTasks.select", { defaultValue: "選択" }) as string}
                    />
                    <input
                      type="text"
                      value={c.title}
                      onChange={(e) =>
                        setCandidates((prev) =>
                          prev.map((p, j) => (i === j ? { ...p, title: e.target.value } : p)),
                        )
                      }
                      className="flex-1 px-2 py-1 border border-border-subtle rounded text-sm font-medium"
                      placeholder={t("ai.extractTasks.titlePlaceholder", { defaultValue: "タスク名" }) as string}
                    />
                    <span className="text-xs text-fg-muted shrink-0">
                      {(c.confidence * 100).toFixed(0)}%
                    </span>
                  </header>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <label className="space-y-1">
                      <span className="text-xs text-fg-muted">
                        {t("ai.extractTasks.assignee", { defaultValue: "担当" }) as string}
                      </span>
                      <select
                        disabled={!canAssign}
                        value={canAssign ? c.assignee ?? "" : currentUserId ?? ""}
                        onChange={(e) =>
                          setCandidates((prev) =>
                            prev.map((p, j) =>
                              i === j ? { ...p, assignee: e.target.value || null } : p,
                            ),
                          )
                        }
                        className="w-full px-2 py-1 border border-border-subtle rounded bg-canvas"
                        data-testid={`task-candidate-assignee-${i}`}
                      >
                        {canAssign ? (
                          <>
                            <option value="">
                              {t("ai.extractTasks.unassigned", { defaultValue: "未割り当て" }) as string}
                            </option>
                            {members.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.displayName}
                              </option>
                            ))}
                          </>
                        ) : (
                          <option value={currentUserId ?? ""}>
                            {members.find((m) => m.id === currentUserId)?.displayName
                              || t("tasks.self", { defaultValue: "自分" }) as string}
                          </option>
                        )}
                      </select>
                      {c.assignee_hint && (
                        <span className="block text-xs text-fg-muted">
                          {t("ai.extractTasks.assigneeHint", {
                            name: c.assignee_hint,
                            defaultValue: "AI の推定: {{name}}",
                          }) as string}
                        </span>
                      )}
                    </label>
                    <label className="space-y-1">
                      <span className="text-xs text-fg-muted">
                        {t("ai.extractTasks.due", { defaultValue: "期日 (yyyy-mm-dd)" }) as string}
                      </span>
                      <input
                        type="text"
                        value={c.due_date_hint ?? ""}
                        onChange={(e) =>
                          setCandidates((prev) =>
                            prev.map((p, j) =>
                              i === j ? { ...p, due_date_hint: e.target.value || null } : p,
                            ),
                          )
                        }
                        className="w-full px-2 py-1 border border-border-subtle rounded"
                        placeholder="2026-05-31"
                        pattern="\d{4}-\d{2}-\d{2}"
                      />
                    </label>
                  </div>
                  <textarea
                    value={c.description ?? ""}
                    onChange={(e) =>
                      setCandidates((prev) =>
                        prev.map((p, j) => (i === j ? { ...p, description: e.target.value } : p)),
                      )
                    }
                    className="w-full px-2 py-1 border border-border-subtle rounded text-sm"
                    rows={2}
                    placeholder={t("ai.extractTasks.descPlaceholder", {
                      defaultValue: "概要 (任意)",
                    }) as string}
                  />
                </article>
              ))}
            </div>
            {candidates.length > 0 && (
              <footer className="px-4 py-3 border-t border-border-subtle flex items-center gap-2">
                <span className="flex-1" />
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 rounded text-fg-secondary hover:bg-surface-raised"
                >
                  {t("common.cancel", { defaultValue: "キャンセル" }) as string}
                </button>
                <button
                  type="button"
                  onClick={() => void confirm()}
                  disabled={status === "creating" || !canCreate}
                  className={cn(
                    "px-4 py-2 rounded bg-brand-600 text-fg-inverse",
                    "hover:bg-brand-700 disabled:bg-fg-subtle",
                    "inline-flex items-center gap-2",
                  )}
                  data-testid="task-extract-confirm"
                >
                  {status === "creating" && (
                    <Loader2 size={14} aria-hidden className="animate-spin" />
                  )}
                  {t("ai.extractTasks.confirm", {
                    count: candidates.filter((c) => c.selected).length,
                    defaultValue: "タスクを作成 ({{count}} 件)",
                  }) as string}
                </button>
              </footer>
            )}
          </div>
        </div>
      )}
    </>
  );
}
